import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OnboardingForm, Missing, useAnswerSetter } from "@/components/onboarding/OnboardingForm";
import { GoLiveResult } from "@/components/onboarding/GoLiveResult";
import { CopyLink } from "@/components/onboarding/CopyLink";
import { INPUT } from "@/components/onboarding/obUi";
import { StatusChip } from "@/pages/AdminOnboarding";
import { ObActions } from "@/components/onboarding/ObActions";

const msg = (e, f) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d[0]?.msg : f;
};

export default function AdminOnboardingForm() {
  const { user } = useAuth();
  const ok = user?.org_type === "internal" && ["super_admin", "admin"].includes(user?.role);
  if (!ok) return <div className="p-8 text-sm" data-testid="onboarding-denied">Only admins can manage buyer onboarding.</div>;
  return <Body />;
}

function Body() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ob, setOb] = useState(null);
  const [a, setA] = useState(null);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoV, setLogoV] = useState(Date.now());
  const set = useAnswerSetter(setA);
  const take = (d) => { setOb(d); setA(d.answers); setPrice(String(d.price_per_lead)); };
  useEffect(() => { api.get(`/admin/onboardings/${id}`).then((r) => take(r.data)).catch(() => setOb(false)); }, [id]);

  const run = async (key, fn, done) => {
    setBusy(key);
    try { const r = await fn(); if (r?.data?.answers) take(r.data); if (done) done(r?.data); return r?.data; }
    catch (e) { toast.error(msg(e, "Something went wrong"), { duration: 9000 }); return null; }
    finally { setBusy(""); }
  };
  const body = { answers: a, price_per_lead: Number(price) || undefined };
  const save = () => run("save", () => api.put(`/admin/onboardings/${id}`, body), () => toast.success("Saved"));
  const goLive = () => run("live", () => api.post(`/admin/onboardings/${id}/go-live`, body), () => { toast.success("Buyer is live"); window.scrollTo({ top: 0 }); });
  const sendBack = (note) => run("back", () => api.post(`/admin/onboardings/${id}/send-back`, { note }),
    (d) => toast.success(d.emailed ? "Sent back and emailed to the buyer" : "Sent back. Copy the link and send it to them."));
  const emailLink = () => run("email", () => api.post(`/admin/onboardings/${id}/email-link`), () => toast.success("Setup link emailed"));
  const remove = () => run("del", () => api.delete(`/admin/onboardings/${id}`), () => { toast.success("Setup link deleted"); nav("/admin/onboarding"); window.scrollTo({ top: 0 }); });
  const uploadLogo = async (file) => {
    setLogoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/onboardings/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOb((o) => ({ ...o, has_logo: data.has_logo }));
      setLogoV(Date.now());
    } catch (e) { toast.error(msg(e, "Couldn't upload the logo")); }
    finally { setLogoBusy(false); }
  };

  if (ob === null) return <div className="p-8 label-mono">Loading…</div>;
  if (ob === false) return <div className="p-8 text-sm text-red-700" data-testid="onboarding-not-found">This setup link wasn&apos;t found.</div>;
  const live = ob.status === "live";
  const logo = { src: ob.has_logo ? `${API}/admin/onboardings/${id}/logo?v=${logoV}` : null, busy: logoBusy, onFile: uploadLogo };
  const priceEdit = live ? null : (
    <input type="number" min={1} step="0.01" className={`${INPUT} max-w-[10rem] font-mono`} value={price} onChange={(e) => setPrice(e.target.value)} data-testid="ob-price-edit" />
  );
  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="admin-onboarding-form-page">
      <Link to="/admin/onboarding" className="inline-flex items-center gap-1 label-mono hover:underline mb-3" data-testid="ob-back"><ArrowLeft size={12} /> All setup links</Link>
      <div className="flex items-center gap-3 flex-wrap mb-2">
        <h1 className="font-chivo font-black text-3xl sm:text-4xl uppercase tracking-tighter leading-none">{a.firm_name || "New buyer"}</h1>
        <StatusChip s={ob.status} testId="ob-status" />
      </div>
      <p className="text-sm text-zinc-700 mb-5 max-w-3xl">
        {live ? "This buyer is set up. Make further changes from Manage Buyer."
          : ob.status === "submitted" ? `The buyer sent this form${ob.terms_acceptance ? ` and agreed to the terms as ${ob.terms_acceptance.name}` : ""}. Check it, fix anything if needed, then approve.`
          : "Fill this in for the buyer, or send them the link below. Nothing goes live until you press the button at the bottom."}
      </p>
      {live && ob.result && <div className="mb-6"><GoLiveResult r={ob.result} states={a.states} /></div>}
      {!live && <div className="mb-6 max-w-2xl"><div className="label-mono mb-1">Private setup link</div><CopyLink path={ob.link_path} testId="ob-link" /></div>}
      <OnboardingForm a={a} set={set} ro={live} admin price={ob.price_per_lead} priceEdit={priceEdit} terms={ob.terms} logo={logo} />
      {!live && (
        <div className="mt-6 space-y-4">
          <Missing items={ob.missing} title="Needed before going live" />
          <ObActions submitted={ob.status === "submitted"} busy={busy} onSave={save} onGoLive={goLive} onSendBack={sendBack} onEmail={emailLink} onDelete={remove} />
        </div>
      )}
    </div>
  );
}
